import { useEffect, useState, useMemo } from 'react';

import { UnsupportedPolicyTypes } from '@generated-gql/generated-types';

import { MockedResponse } from '@nerdwallet/apollo/testing';
import { ApolloError, gql, NetworkStatus } from '@apollo/client';

import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { getLogger } from '@lib/logging';
import { InsuranceType } from '@lib/types/insurance';

type PullResponse = {
  id: string;
  carrierName: string;
  friendlyCarrierName: string;
  policies?: {
    id: string;
    carrierName: string;
    isExpired: boolean;
    policyDurationMonths: number;
    friendlyCarrierName: string;
    policyType: string;
    totalPremiumCents: number;
  }[];
  unsupportedPolicies?: UnsupportedPolicyTypes[];
};

type InsurancePullResponse = {
  insurancePull: {
    success: boolean;
    pull: PullResponse;
  };
};

type UseInsurancePullReturn = {
  error: ApolloError;
  policies?: {
    id: string;
    carrierName: string;
    isExpired: boolean;
    policyDurationMonths: number;
    policyType: string;
    totalPremiumCents: number;
  }[];
  policyTypeCounts?: {
    [InsuranceType.Auto]?: number;
    [InsuranceType.Homeowners]?: number;
    [InsuranceType.Renters]?: number;
  };
  loading: boolean;
  polling: boolean;
  pull?: PullResponse;
};

const logger = getLogger('useInsurancePull');

const GET_INSURANCE_PULL = gql`
  query GetInsurancePull($pullId: ID!) {
    insurancePull(pullId: $pullId) {
      success
      pull {
        id
        carrierName
        friendlyCarrierName
        policies {
          id
          carrierName
          friendlyCarrierName
          isExpired
          policyDurationMonths
          policyType
          totalPremiumCents
        }
        unsupportedPolicies {
          rawValue
          friendlyValue
        }
      }
    }
  }
`;

const POLLING_ATTEMPTS = 10;
const POLLING_DELAY = 4000;

const SUPPORTED_INSURANCE_TYPES = [
  InsuranceType.Auto,
  InsuranceType.Homeowners,
  InsuranceType.Renters,
];

/** Used to fetch insurance pull information from Insurance Management Service. */
export const useInsurancePull = (
  /** Insurance pull ID */
  pullId: string,
  /** Amount of attempts to fetch the insurance pull */
  pollAttempts?: number,
  /** Delay in milliseconds to wait to fetch the insurance pull */
  pollDelay?: number,
): UseInsurancePullReturn => {
  const [remainingAttempts, setRemainingAttempts] = useState(
    (pollAttempts || POLLING_ATTEMPTS) - 1, // Subtract one to account for the first *non-polling* fetch
  );
  const [isPolling, setIsPolling] = useState(true);
  const [prevNetworkStatus, setPrevNetworkStatus] = useState<NetworkStatus>();

  const { data, loading, error, stopPolling, networkStatus } =
    useQueryWithLog<InsurancePullResponse>(logger, GET_INSURANCE_PULL, {
      variables: { pullId },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      pollInterval: pollDelay || POLLING_DELAY,
    });

  const pull = data?.insurancePull?.pull;

  const policies = useMemo(() => {
    const supportedPolicies = pull?.policies.filter((policy) =>
      SUPPORTED_INSURANCE_TYPES.includes(policy.policyType as InsuranceType),
    );

    return supportedPolicies ?? [];
  }, [pull]);

  const policyTypeCounts = useMemo(
    () =>
      policies.reduce((acc, policy) => {
        const policyType = policy.policyType;
        acc[policyType] = (acc[policyType] || 0) + 1;

        return acc;
      }, {}),
    [policies],
  );

  useEffect(
    function decrementRemainingAttempts() {
      setPrevNetworkStatus(networkStatus);

      if (
        prevNetworkStatus !== NetworkStatus.poll &&
        networkStatus === NetworkStatus.poll
      ) {
        setRemainingAttempts(remainingAttempts - 1);
      }
    },
    [networkStatus, prevNetworkStatus, remainingAttempts],
  );

  useEffect(
    function checkIfPollingShouldStop() {
      if (
        remainingAttempts <= 0 ||
        policies.length > 0 ||
        (data?.insurancePull?.success && policies.length === 0)
      ) {
        setIsPolling(false);
        stopPolling();
      }
    },
    [data?.insurancePull, policies, remainingAttempts, stopPolling],
  );

  return {
    polling: isPolling,
    policies,
    policyTypeCounts,
    pull,
    loading,
    error,
  };
};

export const mockInsurancePullResponse = (
  pullId: string,
  hasPolicy: boolean,
  isSuccess: boolean = true,
): MockedResponse => ({
  request: {
    query: GET_INSURANCE_PULL,
    variables: {
      pullId,
    },
  },
  result: {
    data: {
      insurancePull: {
        success: isSuccess,
        pull: {
          id: pullId,
          carrierName: 'NoState',
          friendlyCarrierName: 'No State',
          unsupportedPolicies: hasPolicy
            ? [
                {
                  rawValue: 'UMBRELLA',
                  friendlyValue: 'Umbrella insurance',
                },
                {
                  rawValue: 'MOTORCYCLE',
                  friendlyValue: 'Motorcycle insurance',
                },
                {
                  rawValue: 'FARMOWNER',
                  friendlyValue: 'Farmowner insurance',
                },
                {
                  rawValue: 'LANDLORD',
                  friendlyValue: 'Landlord insurance',
                },
                {
                  rawValue: 'RECREATIONAL_VEHICLE',
                  friendlyValue: 'Recreational vehicle insurance',
                },
                {
                  rawValue: 'TERM_LIFE',
                  friendlyValue: 'Term life insurance',
                },
              ]
            : [],
          policies: hasPolicy
            ? [
                {
                  id: 'valid-auto-insurance-policy',
                  policyType: InsuranceType.Auto,
                  carrierName: 'NoState',
                },
                {
                  id: 'valid-renters-insurance-policy',
                  policyType: InsuranceType.Renters,
                  carrierName: 'NoState',
                },
              ]
            : [],
          policyTypes: {
            [InsuranceType.Auto]: 1,
            [InsuranceType.Renters]: 1,
          },
        },
      },
    },
  },
});
